import { useEffect, useState } from "react";
import Button, { ButtonType } from "../../components/Assets/Button";
import { fetchProducts } from "../../store/slices/productSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  addProductSn,
  fetchLastSerialNumber,
  getProductSn,
} from "../../store/slices/productsSnSlice";
import toast from "react-hot-toast";
// Add Max Count to 100
const max = 100;
const SNManagement = () => {
  const [loading, setLoading] = useState(false);
  const [defaultProductId, setDefaultProductId] = useState<string>("");
  const lastSerial = useSelector(
    (state: RootState) => state.productSn.lastSerialNumber
  );

  const [count, setCount] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [serials, setSerials] = useState<string[]>([]);
  const [productSelections, setProductSelections] = useState<string[]>([]);
  const [prevDefaultProductId, setPrevDefaultProductId] = useState<string>("");

  const dispatch: RootDispatch = useDispatch();
  const products = useSelector((state: RootState) => state.product.products);

  useEffect(() => {
    if (!confirmed) return;

    const updatedSelections = productSelections.map((sel) =>
      sel === "" || sel === prevDefaultProductId ? defaultProductId : sel
    );

    setProductSelections(updatedSelections);
    setPrevDefaultProductId(defaultProductId);
  }, [confirmed, defaultProductId, prevDefaultProductId, productSelections]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchProducts()).unwrap();
      const productIds = products?.map((p) => p._id);
      if (productIds.length) {
        dispatch(getProductSn({ productIds }));
      }
    };
    dispatch(fetchLastSerialNumber());
    fetchData();
  }, [dispatch, products]);

  const handleConfirmCount = async () => {
    if (count! <= 0) return toast.error("Please enter a valid number");
    if (count! > max) return toast.error(`Max count is ${max}`);

    let prefix = "250410";
    let lastCounter = 0;

    if (lastSerial && lastSerial.length === 11) {
      prefix = lastSerial.slice(0, 6);
      lastCounter = Number(lastSerial.slice(6));
    }

    const snList: string[] = [];

    for (let i = 0; i < count!; i++) {
      lastCounter += 1;
      const counterStr = String(lastCounter).padStart(5, "0");
      snList.push(`${prefix}${counterStr}`);
    }

    setSerials(snList);
    setProductSelections(Array(count!).fill(defaultProductId));
    setConfirmed(true);
    setPrevDefaultProductId(defaultProductId);
  };

  const handleProductChange = (index: number, value: string) => {
    const updated = [...productSelections];
    updated[index] = value;
    setProductSelections(updated);
  };

  const handleAddAll = async () => {
    for (let i = 0; i < count!; i++) {
      if (!serials[i] || (!productSelections[i] && !defaultProductId)) {
        toast.error(`Product ${i + 1} is not selected`);
        return;
      }
    }

    setLoading(true);

    for (let i = 0; i < count!; i++) {
      await dispatch(
        addProductSn({
          productId: productSelections[i] || defaultProductId,
          serialNumber: serials[i],
        })
      );
    }
    await dispatch(fetchLastSerialNumber());

    setSerials([]);
    setProductSelections([defaultProductId]);
    setCount(null);
    setConfirmed(false);
    setDefaultProductId("");

    setLoading(false);
    toast.success("All serial numbers added successfully!");
  };

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs">
        <h2 className="text-md font-semibold text-gray-800 mb-3">
          Serial Number Generator
        </h2>

        <div className="space-y-3">
          <select
            className="w-full p-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 text-sm"
            value={defaultProductId}
            onChange={(e) => setDefaultProductId(e.target.value)}
          >
            <option value="">Default Product</option>
            {products?.map((product) => (
              <option key={product._id} value={product._id}>
                {product.productName}
              </option>
            ))}
          </select>

          <div className="flex gap-2 items-center">
            <input
              type="number"
              placeholder="Number of serials"
              value={count || ""}
              onChange={(e) => setCount(Number(e.target.value))}
              className="flex-1 p-2 border border-gray-200 rounded-md text-sm"
            />
            <Button
              type={ButtonType.Submit}
              onClick={handleConfirmCount}
              className="text-sm px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>

      {confirmed && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Generated Serials
            </h3>
          </div>

          <div className="space-y-2">
            {serials?.map((sn, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center bg-blue-50 text-blue-600 text-xs font-medium rounded-md">
                  {idx + 1}
                </div>
                <input
                  type="text"
                  value={sn}
                  onChange={(e) => {
                    const updatedSerials = [...serials];
                    updatedSerials[idx] = e.target.value;
                    setSerials(updatedSerials);
                  }}
                  className="flex-1 p-1.5 border border-gray-200 rounded-md bg-gray-50 text-xs font-mono"
                />
                <select
                  className="flex-1 p-1.5 border border-gray-200 rounded-md text-xs"
                  value={productSelections[idx] || defaultProductId}
                  onChange={(e) => handleProductChange(idx, e.target.value)}
                >
                  <option value="">Select Product</option>
                  {products?.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.productName}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {!loading && (
            <Button
              type={ButtonType.Submit}
              onClick={handleAddAll}
              className="mt-4 w-full text-sm px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md"
            >
              Add All {serials?.length} Serials
            </Button>
          )}
          {loading && (
            <Button
              type={ButtonType.Submit}
              onClick={() => {}}
              className="mt-4 w-full text-sm px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md"
            >
              Adding... ({serials?.length})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SNManagement;
